/**
 * Who is signed in, and what the UI may offer them.
 *
 * Two things live here and nowhere else:
 *
 * 1. **The session probe.** On mount the app asks `/auth/me`. A 401 is not an
 *    error — it is the normal answer for "nobody is signed in yet" — so it
 *    resolves to `user: null` and the app renders the login screen.
 *
 * 2. **Permissions.** The server sends them; the UI does not derive them from a
 *    role string. Hiding a button is cosmetic — the API enforces every rule — but
 *    deriving it in two places is how the two drift apart, and then the UI offers
 *    an action that always fails.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, authApi } from '../api';

const AuthContext = createContext(null);

const NO_PERMISSIONS = {
  manage_users: false,
  manage_organizations: false,
  manage_api_keys: false,
  see_all_in_organization: false,
  cross_organization: false,
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(NO_PERMISSIONS);
  const [checking, setChecking] = useState(true);

  const apply = useCallback((payload) => {
    setUser(payload?.user ?? null);
    setPermissions(payload?.permissions ?? NO_PERMISSIONS);
  }, []);

  const probe = useCallback(async () => {
    try {
      apply(await authApi.me());
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        // Expected on a cold load. Not surfaced as a failure.
        apply(null);
      } else {
        apply(null);
        // A network or server fault is worth knowing about, unlike a 401.
        if (!(error instanceof ApiError) || error.code === 'network_error') {
          console.warn('Could not check the session:', error.message);
        }
      }
    } finally {
      setChecking(false);
    }
  }, [apply]);

  useEffect(() => {
    probe();
  }, [probe]);

  const login = useCallback(
    async (username, password, organization) => {
      apply(await authApi.login(username, password, organization));
    },
    [apply],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // Clear locally whatever the server said. A failed logout call must not
      // leave the UI looking signed in.
      apply(null);
    }
  }, [apply]);

  const value = useMemo(
    () => ({
      user,
      permissions,
      checking,
      isSignedIn: Boolean(user),
      isAdmin: Boolean(permissions.manage_users),
      isSuperadmin: Boolean(permissions.manage_organizations),
      organization: user?.organization ?? null,
      login,
      logout,
      refresh: probe,
    }),
    [user, permissions, checking, login, logout, probe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
